import { useCallback, useEffect, useRef, useState } from 'react'
import { useT } from '../../../lib/i18n'
import styles from './Composer.module.css'

/**
 * THE COMPOSER — you always write first, and you always write next.
 *
 * The one control on the surface. It is never disabled while a reply is coming:
 * input is never gated (the will contract), and a person who wants to add a line
 * before the other has answered is doing an ordinary thing that every messaging
 * app allows.
 *
 * The textarea grows with the text rather than scrolling inside a fixed box,
 * because the alternative — a one-line slot — quietly teaches that short is
 * correct, and the one thing this surface is for is saying more than you meant to.
 */
/** How often the typing signal is repeated while the composer has text; the engine's hold outlasts one miss. */
const TYPING_EVERY_MS = 4000

export default function Composer({
  onSend,
  onTyping,
  disabled = false,
  paper = false,
}: {
  onSend: (text: string) => void
  /**
   * THE TYPING SIGNAL (2026-09-22; the live stretch): true on the first keystroke into an empty field and every
   * few seconds after, false when the field empties. What a person at the other end does with the dots is theirs;
   * here it only says they are there.
   */
  onTyping?: (typing: boolean) => void
  /** Only before the session is live. Never while waiting for a reply. */
  disabled?: boolean
  /** Render as a ruled line on a paper strip rather than as a filled field. */
  paper?: boolean
}) {
  const t = useT()
  const [text, setText] = useState('')
  const ref = useRef<HTMLTextAreaElement | null>(null)

  const wasTyping = useRef(false)
  const typingTimer = useRef<number | null>(null)
  useEffect(() => {
    const typing = text.trim().length > 0
    if (typing && !wasTyping.current) {
      wasTyping.current = true
      onTyping?.(true)
      typingTimer.current = window.setInterval(() => onTyping?.(true), TYPING_EVERY_MS)
    } else if (!typing && wasTyping.current) {
      wasTyping.current = false
      if (typingTimer.current !== null) window.clearInterval(typingTimer.current)
      typingTimer.current = null
      onTyping?.(false)
    }
  }, [text, onTyping])
  useEffect(
    () => () => {
      if (typingTimer.current !== null) window.clearInterval(typingTimer.current)
    },
    [],
  )

  const grow = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [])

  const submit = useCallback(() => {
    const body = text.trim()
    if (!body || disabled) return
    onSend(body)
    setText('')
    // Reset the grown height with the cleared value.
    requestAnimationFrame(() => {
      if (ref.current) ref.current.style.height = 'auto'
    })
  }, [text, disabled, onSend])

  return (
    <div className={paper ? `${styles.bar} ${styles.onPaper}` : styles.bar}>
      <textarea
        ref={ref}
        className={styles.input}
        value={text}
        rows={1}
        disabled={disabled}
        placeholder=""
        aria-label={t('thread.write')}
        onChange={(e) => {
          setText(e.target.value)
          grow()
        }}
        onKeyDown={(e) => {
          // Enter sends; Shift+Enter breaks the line. On a phone the on-screen
          // return key inserts a newline instead, which is what thumbs expect.
          if (e.key === 'Enter' && !e.shiftKey && !isTouch()) {
            e.preventDefault()
            submit()
          }
        }}
      />
      <button
        className={styles.send}
        onClick={submit}
        disabled={disabled || !text.trim()}
        aria-label={t('thread.send')}
      >
        <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
          <path
            d="M4 12h15M13 6l6 6-6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  )
}

function isTouch(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
}
